// =============================================================================
// MonthlyLineChart — gráfica de líneas SVG nativa (12 meses)
// Reutilizable para revenue y para contador de presupuestos.
// =============================================================================

import type { MonthPoint } from '../../../types/dashboard';

interface MonthlyLineChartProps {
  data: MonthPoint[];
  height?: number;
  color?: string;
  /** Función de formato para el valor en la tooltip/eje Y */
  formatValue?: (v: number) => string;
  /** Etiqueta para el título superior */
  title?: string;
}

export function MonthlyLineChart({
  data,
  height = 180,
  color = '#008DD0',
  formatValue = (v) => String(v),
  title,
}: MonthlyLineChartProps) {
  const width = 560;
  const padding = { top: 16, right: 16, bottom: 32, left: 48 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const values = data.map((d) => d.value);
  const maxVal = Math.max(1, ...values);
  const minVal = 0;

  // Escalas
  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const yScale = (v: number) =>
    padding.top + innerH - ((v - minVal) / (maxVal - minVal || 1)) * innerH;
  const xScale = (i: number) => padding.left + i * xStep;

  // Líneas guía (4 divisiones)
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    y: padding.top + innerH * (1 - p),
    value: minVal + (maxVal - minVal) * p,
  }));

  // Puntos y path
  const points = data.map((d, i) => ({
    x: xScale(i),
    y: yScale(d.value),
    data: d,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${padding.top + innerH} L ${points[0].x.toFixed(1)} ${padding.top + innerH} Z`
      : '';

  const hasData = values.some((v) => v > 0);

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-semibold text-slate-700 mb-2">{title}</h3>
      )}
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          preserveAspectRatio="xMidYMid meet"
          style={{ minWidth: 360 }}
        >
          {/* Grid */}
          {gridLines.map((g, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={g.y}
                y2={g.y}
                stroke="#e2e8f0"
                strokeDasharray="2 3"
              />
              <text
                x={padding.left - 6}
                y={g.y}
                textAnchor="end"
                dominantBaseline="middle"
                style={{ fontSize: 10, fill: '#94a3b8' }}
              >
                {formatValue(Math.round(g.value))}
              </text>
            </g>
          ))}

          {/* Área */}
          {hasData && (
            <path d={areaPath} fill={color} opacity={0.1} />
          )}

          {/* Línea */}
          {hasData && (
            <path
              d={linePath}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* Puntos */}
          {hasData && points.map((p, i) => (
            <g key={i}>
              <circle
                cx={p.x}
                cy={p.y}
                r={3.5}
                fill="white"
                stroke={color}
                strokeWidth={2}
              >
                <title>{`${p.data.label}: ${formatValue(p.data.value)}`}</title>
              </circle>
            </g>
          ))}

          {/* Etiquetas eje X */}
          {points.map((p, i) => (
            <text
              key={i}
              x={p.x}
              y={height - padding.bottom + 16}
              textAnchor="middle"
              style={{ fontSize: 10, fill: '#64748b' }}
            >
              {p.data.label}
            </text>
          ))}

          {!hasData && (
            <text
              x={width / 2}
              y={height / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{ fontSize: 13, fill: '#94a3b8' }}
            >
              Sin datos en este periodo
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}
